import { SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Empty, Input, Spin } from 'antd';
import { debounce, get, map, trim, uniqBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import useLazyQueryWithCallbacks from '../common/CommonUseLazyQuery';
import { SCROLL_CONST } from '../common/constants';
import { formatDisplayText } from '../common/utils';

const FILTER_OPTIONS_LIMIT = 25;
let searchDebounce = null;

const TableFilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  query,
  variables = {},
  responsePath,
  valuePath = 'value',
  labelPath = 'value',
  fetchPolicy = 'network-only',
  projectAgencyIds,
  onQueryCompleted,
  useEffectDeps = [],
  fieldType,
  visible,
}) => {
  const [searchText, setSearchText] = useState('');
  const [localSelected, setLocalSelected] = useState(selectedKeys || []);
  const [filterOptions, setFilterOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const scrollContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  const [fetchFilterOptions] = useLazyQueryWithCallbacks(query, {
    fetchPolicy,
    onCompleted: (res) => {
      const data = get(res, responsePath, []);
      const values = data?.values || data || [];

      const newOptions = map(values, (item) => {
        const text = typeof item === 'object' ? get(item, labelPath) : item;
        return {
          text: formatDisplayText(fieldType, text),
          value: typeof item === 'object' ? get(item, valuePath) : item,
        };
      });

      if (currentPage === 0) {
        setFilterOptions(newOptions);
      } else {
        setFilterOptions((prev) => uniqBy([...prev, ...newOptions], 'value'));
      }

      setCurrentPage((page) => page + 1);
      setHasMore(values?.length >= FILTER_OPTIONS_LIMIT);
      setLoading(false);

      if (typeof onQueryCompleted === 'function') {
        onQueryCompleted(res);
      }
    },
    onError: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    setLocalSelected(selectedKeys || []);
  }, [selectedKeys]);

  useEffect(() => {
    let timeoutId;

    if (visible) {
      timeoutId = setTimeout(() => {
        searchInputRef?.current?.focus?.();
      }, 100);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [visible]);

  useEffect(() => {
    if (query) {
      setCurrentPage(0);
      setSearchText('');
      setFilterOptions([]);
      setLoading(true);
      fetchFilterOptions({
        variables: {
          ...variables,
          filter: {
            ...(variables?.filter || {}),
            skip: 0,
            limit: FILTER_OPTIONS_LIMIT,
            search: undefined,
            projectAgencyIds:
              projectAgencyIds?.length > 0 ? projectAgencyIds : undefined,
          },
        },
      });
    }
  }, [query, projectAgencyIds, ...useEffectDeps]);

  const searchQuery = (search) => {
    setSearchText(trim(search));
    setCurrentPage(0);
    setFilterOptions([]);
    setLoading(true);
    fetchFilterOptions({
      fetchPolicy: 'network-only',
      variables: {
        ...variables,
        filter: {
          ...(variables?.filter || {}),
          search: trim(search) || undefined,
          skip: 0,
          limit: FILTER_OPTIONS_LIMIT,
          projectAgencyIds:
            projectAgencyIds?.length > 0 ? projectAgencyIds : undefined,
        },
      },
    });
  };

  const handleSearch = (e) => {
    const value = e?.target?.value || '';
    setSearchText(value);
    if (searchDebounce) {
      searchDebounce.cancel();
      searchDebounce = null;
    }
    searchDebounce = debounce(searchQuery, 500);
    searchDebounce(trim(value));
  };

  const handleClear = () => {
    if (searchText) {
      searchQuery('');
    }
  };

  const handleScroll = (e) => {
    const { target } = e;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};
    const scrolledToBottom =
      scrollTop + offsetHeight >= scrollHeight - SCROLL_CONST;

    if (scrolledToBottom && hasMore && !loading) {
      setLoading(true);
      fetchFilterOptions({
        variables: {
          ...variables,
          filter: {
            ...(variables?.filter || {}),
            skip: currentPage * FILTER_OPTIONS_LIMIT,
            limit: FILTER_OPTIONS_LIMIT,
            search: trim(searchText) || undefined,
            projectAgencyIds:
              projectAgencyIds?.length > 0 ? projectAgencyIds : undefined,
          },
        },
      });
    }
  };

  const handleCheckboxChange = (value, checked) => {
    let newSelected;
    if (checked) {
      newSelected = [...localSelected, value];
    } else {
      newSelected = localSelected?.filter((v) => v !== value);
    }
    setLocalSelected(newSelected);
    setSelectedKeys(newSelected);
  };

  const handleOk = () => {
    confirm();
  };

  const handleReset = () => {
    setLocalSelected([]);
    setSelectedKeys([]);
    setSearchText('');
    clearFilters?.();
    handleClear();
    confirm();
  };

  return (
    <div className="table-filter-dropdown">
      <div className="table-filter-dropdown-search">
        <Input
          ref={searchInputRef}
          placeholder="Search in filters"
          prefix={<SearchOutlined className="text-secondary" />}
          value={searchText}
          onChange={handleSearch}
          onClear={handleClear}
          allowClear
        />
      </div>

      <div
        className="table-filter-dropdown-options"
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {filterOptions?.length > 0 ? (
          map(filterOptions, (option) => (
            <div
              key={option?.value}
              className={`table-filter-dropdown-option ${
                localSelected?.includes(option?.value) ? 'selected' : ''
              }`}
            >
              <Checkbox
                checked={localSelected?.includes(option?.value)}
                onChange={(e) =>
                  handleCheckboxChange(option?.value, e?.target?.checked)
                }
              >
                {option?.text || option?.value}
              </Checkbox>
            </div>
          ))
        ) : (
          !loading && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        )}
        {loading && (
          <div className="d-flex justify-center">
            <Spin size='small' spinning />
          </div>
        )}
      </div>

      <div className="table-filter-dropdown-actions">
        <Button size="small" onClick={handleReset}>
          Reset
        </Button>
        <Button type="primary" size="small" onClick={handleOk}>
          OK
        </Button>
      </div>
    </div>
  );
};

export default TableFilterDropdown;
